import { ChangeEvent, useCallback, useState, useRef } from 'react';
import * as tus from 'tus-js-client';
import { Auth } from "aws-amplify";
import API from "../../config/api";
import { CheckCircleIcon } from '@heroicons/react/20/solid';

/**
 * Retrieves the upload link for a video of the specified size.
 * @param size - The size of the video in bytes.
 * @returns A Promise that resolves to the upload link.
 */
async function getUploadLink(size: number | any): Promise<any> {
  try {
    size = size || 0;
    const data = await Auth.currentSession();
    const idToken = data.getIdToken().getJwtToken();
    const response = await API.get(`/uploadlink?size=${size}`, {
      headers: {
        Authorization: idToken,
      }
    });
    const res = response.data;
    console.log("Response from API: " + res);
    return res;
  } catch (error) {
    console.log(error);
  }
}

const VideoUploader = () => {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<string>('');
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);
  const [isUploadComplete, setIsUploadComplete] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.item(0);
    if (selectedFile) {
      setFile(selectedFile);
      setIsUploadComplete(false); // Reset the finish button visibility
    }
  }, []);

  const handleUploadStart = useCallback(async () => {
    if (!file) {
      console.log("No file selected");
      return;
    }

    setProgress(`PROCESSING FILE: ${file.name} (${file.size} BYTES), PLEASE WAIT.`);
    
    const response = await getUploadLink(file.size);
    if (!response || !response.upload || !response.upload.upload_link) {
      setProgress("FAILED TO GET UPLOAD LINK. PLEASE TRY AGAIN.");
      return;
    }
    
    setProgress("PREPARING TO UPLOAD FILE");

    const upload = new tus.Upload(file, {
      uploadUrl: response.upload.upload_link,
      endpoint: response.upload.upload_link,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: {
        filename: file.name,
        filetype: file.type,
      },
      onError(error) {
        console.error(`Failed because: ${error}`);
        setProgress("FAILED TO UPLOAD FILE. CHECK LOGS, REFRESH, AND TRY AGAIN.");
      },
      onProgress(bytesUploaded, bytesTotal) {
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        console.log(bytesUploaded, bytesTotal, `${percentage}%`);
        setProgress(`${percentage}%`);
        setUploadPercentage(parseFloat(percentage));
      },
      onSuccess() {
        console.log('Download from %s', upload.url);
        setProgress("UPLOAD COMPLETE. READY FOR ANOTHER UPLOAD!");
        setUploadPercentage(100);
        setFile(null);
        setIsUploadComplete(true); // Show the finish button when upload is complete
      },
    });
    upload.start();
  }, [file]);

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-x-1.5 rounded-md bg-green-800 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-800"
        disabled={!!file} // Disable button if a file is selected
      >
        <CheckCircleIcon className="-ml-0.5 h-5 w-5 bg-green-800" aria-hidden="true" />
        {file ? `Selected: ${file.name}` : "Select file to upload"}
      </button>
      {file && (
        <button
          type="button"
          onClick={handleUploadStart}
          className="relative ml-5 inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        >
          <CheckCircleIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Confirm upload
          {uploadPercentage > 0 && (
            <div className="absolute bottom-0 left-0 h-1.5 bg-green-800" style={{ width: `${uploadPercentage}%` }} />
          )}
        </button>
      )}

      {isUploadComplete && (
        <button
          type="button"
          onClick={() => console.log('Finish button clicked')}
          className="relative ml-5 inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        >
          <CheckCircleIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Finish
        </button>
      )}

      <h6 className="uppercase font-bold">{progress}</h6>
      <div className="mx-auto max-w-2xl text-center">
        <h1 className="mb-3 mt-5 text-lg leading-8 text-black-600">
          After the upload is completed, the video will be submitted and considered
          for the Top Live. The submission will be counted against remaining
          uploads in the uploader pass. Also, the video and details can be viewed
          on the channel page.
        </h1>
        <b>Note:</b>
        <ul className="mx-auto max-w-2xl text-center max-w-md space-y-1 text-black-500 list-disc list-inside dark:text-gray-400">
          <li>Video length limit is 15 mins</li>
          <li>Video bitrate limit is X MBPs</li>
          <li>Once submitted, terms and conditions will apply to the uploaded content.</li>
          <li>Please avoid any violations of copyrights and privacy rights, and do not post any stolen content.</li>
          <li>It is not recommended to upload the same content multiple times, as it can negatively impact your performance in the Top Live.</li>
        </ul>
      </div>
    </div>
  );
};

export default VideoUploader;
